import React from 'react';
import { useTranslation } from 'react-i18next';

interface RequestFormProps {
  requestForm: any;
  setRequestForm: (form: any) => void;
  isSubmitting: boolean;
  submitError: string | null;
  submitSuccess: boolean;
  handleRequestSubmit: (e: React.FormEvent) => void;
}

const RequestForm: React.FC<RequestFormProps> = ({ requestForm, setRequestForm, isSubmitting, submitError, submitSuccess, handleRequestSubmit }) => {
  const { t } = useTranslation();
  return (
    <section id="request" className="py-20 bg-primary-light dark:bg-primary-dark relative overflow-hidden">
      <div className="container mx-auto px-6 relative z-10">
        <div className="max-w-3xl mx-auto">
          <h2 className="text-4xl font-bold text-center mb-8">
            {String(t('request.title'))}
          </h2>
          <p className="text-gray-600 dark:text-gray-400 text-center mb-12">
            {String(t('request.subtitle'))}
          </p>
          <form onSubmit={handleRequestSubmit} className="space-y-8">
            {/* Equipment Type */}
            <div className="space-y-4">
              <label className="block text-lg font-medium">
                {String(t('request.equipmentType'))}
              </label>
              <select
                className="w-full px-4 py-3 rounded-lg bg-white/50 dark:bg-black/50 backdrop-blur-sm border border-gray-200 dark:border-gray-800 focus:ring-2 focus:ring-accent focus:border-transparent transition-colors"
                value={requestForm.equipmentType}
                onChange={e => {
                  setRequestForm((prev: any) => ({ ...prev, equipmentType: e.target.value }));
                  const otherInput = document.getElementById('otherEquipment');
                  if (otherInput) {
                    otherInput.style.display = e.target.value === 'other' ? 'block' : 'none';
                  }
                }}
                required
              >
                <option value="">{String(t('request.selectType'))}</option>
                {(() => {
                  const equipmentKeys = [
                    'balerLargeSquare',
                    'balerSmallSquare2',
                    'balerSmallSquare3',
                    'dewpoint',
                    'planter',
                    'rake',
                    'tractor',
                    'other'
                  ];
                  return [...equipmentKeys]
                    .sort((a, b) => {
                      if (b === 'other') return -1;
                      const textA = String((t as any)(`request.${a}`));
                      const textB = String((t as any)(`request.${b}`));
                      return textA.localeCompare(textB, 'en');
                    })
                    .map(key => (
                      <option key={key} value={key}>
                        {String((t as any)(`request.${key}`))}
                      </option>
                    ));
                })()}
              </select>
            </div>
            {/* Other Equipment Input */}
            <div id="otherEquipment" className="space-y-4 hidden">
              <input
                type="text"
                className="w-full px-4 py-3 rounded-lg bg-white/50 dark:bg-black/50 backdrop-blur-sm border border-gray-200 dark:border-gray-800 focus:ring-2 focus:ring-accent focus:border-transparent transition-colors"
                placeholder={String(t('request.otherPlaceholder'))}
                value={requestForm.otherEquipment}
                onChange={e => setRequestForm((prev: any) => ({ ...prev, otherEquipment: e.target.value }))}
              />
            </div>
            {/* Budget Range */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-4">
                <label className="block text-lg font-medium">
                  {String(t('request.minBudget'))}
                </label>
                <div className="relative">
                  <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-500">$</span>
                  <input
                    type="text"
                    className="w-full pl-8 pr-4 py-3 rounded-lg bg-white/50 dark:bg-black/50 backdrop-blur-sm border border-gray-200 dark:border-gray-800 focus:ring-2 focus:ring-accent focus:border-transparent transition-colors"
                    value={requestForm.minBudget}
                    onChange={e => setRequestForm((prev: any) => ({ ...prev, minBudget: e.target.value }))}
                  />
                </div>
              </div>
              <div className="space-y-4">
                <label className="block text-lg font-medium">
                  {String(t('request.maxBudget'))}
                </label>
                <div className="relative">
                  <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-500">$</span>
                  <input
                    type="text"
                    className="w-full pl-8 pr-4 py-3 rounded-lg bg-white/50 dark:bg-black/50 backdrop-blur-sm border border-gray-200 dark:border-gray-800 focus:ring-2 focus:ring-accent focus:border-transparent transition-colors"
                    value={requestForm.maxBudget}
                    onChange={e => setRequestForm((prev: any) => ({ ...prev, maxBudget: e.target.value }))}
                  />
                </div>
              </div>
            </div>
            {/* Target Date */}
            <div className="space-y-4">
              <label className="block text-lg font-medium">
                {String(t('request.targetDate'))}
              </label>
              <input
                type="date"
                className="w-full px-4 py-3 rounded-lg bg-white/50 dark:bg-black/50 backdrop-blur-sm border border-gray-200 dark:border-gray-800 focus:ring-2 focus:ring-accent focus:border-transparent transition-colors"
                value={requestForm.targetDate}
                onChange={e => setRequestForm((prev: any) => ({ ...prev, targetDate: e.target.value }))}
              />
            </div>
            {/* Additional Details */}
            <div className="space-y-4">
              <label className="block text-lg font-medium">
                {String(t('request.details'))}
              </label>
              <textarea
                rows={4}
                className="w-full px-4 py-3 rounded-lg bg-white/50 dark:bg-black/50 backdrop-blur-sm border border-gray-200 dark:border-gray-800 focus:ring-2 focus:ring-accent focus:border-transparent transition-colors resize-none"
                placeholder={String(t('request.detailsPlaceholder'))}
                value={requestForm.details}
                onChange={e => setRequestForm((prev: any) => ({ ...prev, details: e.target.value }))}
                required
              ></textarea>
            </div>
            {/* Contact Information */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-4">
                <label className="block text-lg font-medium">
                  {String(t('request.name'))}
                </label>
                <input
                  type="text"
                  className="w-full px-4 py-3 rounded-lg bg-white/50 dark:bg-black/50 backdrop-blur-sm border border-gray-200 dark:border-gray-800 focus:ring-2 focus:ring-accent focus:border-transparent transition-colors"
                  value={requestForm.name}
                  onChange={e => setRequestForm((prev: any) => ({ ...prev, name: e.target.value }))}
                  required
                />
              </div>
              <div className="space-y-4">
                <label className="block text-lg font-medium">
                  {String(t('request.whatsapp'))}
                </label>
                <input
                  type="tel"
                  className="w-full px-4 py-3 rounded-lg bg-white/50 dark:bg-black/50 backdrop-blur-sm border border-gray-200 dark:border-gray-800 focus:ring-2 focus:ring-accent focus:border-transparent transition-colors"
                  value={requestForm.whatsapp}
                  onChange={e => setRequestForm((prev: any) => ({ ...prev, whatsapp: e.target.value }))}
                />
              </div>
            </div>
            <div className="grid grid-cols-1 gap-6">
              <div className="space-y-4">
                <label className="block text-lg font-medium">
                  {String(t('request.email'))}
                </label>
                <input
                  type="email"
                  className="w-full px-4 py-3 rounded-lg bg-white/50 dark:bg-black/50 backdrop-blur-sm border border-gray-200 dark:border-gray-800 focus:ring-2 focus:ring-accent focus:border-transparent transition-colors"
                  value={requestForm.email}
                  onChange={e => setRequestForm((prev: any) => ({ ...prev, email: e.target.value }))}
                  required
                />
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-4">
                <label className="block text-lg font-medium">
                  {String(t('request.location'))}
                </label>
                <input
                  type="text"
                  className="w-full px-4 py-3 rounded-lg bg-white/50 dark:bg-black/50 backdrop-blur-sm border border-gray-200 dark:border-gray-800 focus:ring-2 focus:ring-accent focus:border-transparent transition-colors"
                  value={requestForm.location}
                  onChange={e => setRequestForm((prev: any) => ({ ...prev, location: e.target.value }))}
                  required
                />
              </div>
            </div>
            {submitError && (
              <div className="text-red-500 text-center">
                {submitError}
              </div>
            )}
            {submitSuccess && (
              <div className="text-green-500 text-center">
                {String(t('request.successMessage'))}
              </div>
            )}
            <div className="text-center">
              <button
                type="submit"
                disabled={isSubmitting}
                className="bg-accent text-white px-8 py-3 rounded-full text-lg font-medium hover:bg-opacity-90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSubmitting ? (
                  String(t('request.submitting'))
                ) : (
                  String(t('request.submit'))
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </section>
  );
};

export default RequestForm;
